import React, { useMemo, useRef, useState, useEffect } from 'react';
import { diffLines, diffWords, Change } from 'diff';

interface CustomDiffViewerProps {
  oldContent: string;
  newContent: string;
  showDiffOnly?: boolean;
}

const CustomDiffViewer: React.FC<CustomDiffViewerProps> = ({ oldContent, newContent, showDiffOnly = true }) => {
  const [expandedFolds, setExpandedFolds] = useState<Set<number>>(new Set());
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const isSyncing = useRef(false);

  const { lines } = useMemo(() => {
    const lineChanges = diffLines(oldContent, newContent);
    const processedLines: { 
      leftNumber?: number; 
      rightNumber?: number; 
      type: 'added' | 'removed' | 'common' | 'modified'; 
      leftContent?: React.ReactNode; 
      rightContent?: React.ReactNode; 
      leftRaw?: string;
      rightRaw?: string;
    }[] = [];
    let leftLineNumber = 1;
    let rightLineNumber = 1;

    for (let i = 0; i < lineChanges.length; i++) {
      const part = lineChanges[i];
      const nextPart = lineChanges[i + 1];

      if (part.removed && nextPart && nextPart.added) {
        const removedLines = part.value.replace(/\n$/, '').split('\n');
        const addedLines = nextPart.value.replace(/\n$/, '').split('\n');
        const maxLength = Math.max(removedLines.length, addedLines.length);

        for (let j = 0; j < maxLength; j++) {
          const leftContent = removedLines[j] || '';
          const rightContent = addedLines[j] || '';

          const wordChanges = diffWords(leftContent, rightContent);
          const leftStyled = wordChanges.filter(p => !p.added).map((p, idx) => (
            <span key={idx} style={{ backgroundColor: p.removed ? '#fdb8c0' : 'transparent' }}>{p.value}</span>
          ));
          const rightStyled = wordChanges.filter(p => !p.removed).map((p, idx) => (
            <span key={idx} style={{ backgroundColor: p.added ? '#acf2bd' : 'transparent' }}>{p.value}</span>
          ));

          processedLines.push({
            type: 'modified',
            leftNumber: leftContent !== undefined ? leftLineNumber++ : undefined,
            rightNumber: rightContent !== undefined ? rightLineNumber++ : undefined,
            leftContent: leftStyled,
            rightContent: rightStyled,
            leftRaw: leftContent,
            rightRaw: rightContent,
          });
        }
        i++; // Skip the next part as it has been processed
      } else {
        const lines = part.value.replace(/\n$/, '').split('\n');
        lines.forEach((line: string) => {
          if (part.added) {
            processedLines.push({ rightNumber: rightLineNumber++, type: 'added', rightContent: line, rightRaw: line });
          } else if (part.removed) {
            processedLines.push({ leftNumber: leftLineNumber++, type: 'removed', leftContent: line, leftRaw: line });
          } else {
            processedLines.push({ leftNumber: leftLineNumber++, rightNumber: rightLineNumber++, type: 'common', leftContent: line, rightContent: line, leftRaw: line, rightRaw: line });
          }
        });
      }
    }

    return { lines: processedLines };
  }, [oldContent, newContent]);

  useEffect(() => {
    setExpandedFolds(new Set());
  }, [oldContent, newContent, showDiffOnly]);

  const handleScroll = (panel: 'left' | 'right') => {
    if (isSyncing.current) return;

    isSyncing.current = true;

    const leftPanel = leftPanelRef.current;
    const rightPanel = rightPanelRef.current;

    if (leftPanel && rightPanel) {
      if (panel === 'left') {
        rightPanel.scrollTop = leftPanel.scrollTop;
      } else {
        leftPanel.scrollTop = rightPanel.scrollTop;
      }
    }

    // Use a timeout to prevent scroll events from firing too rapidly
    setTimeout(() => {
      isSyncing.current = false;
    }, 50);
  };

  const handleCopy = (e: React.ClipboardEvent, side: 'left' | 'right') => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    e.preventDefault();

    const range = selection.getRangeAt(0);
    const savedRange = range.cloneRange(); // Save for restoration

    const panelRef = side === 'left' ? leftPanelRef : rightPanelRef;
    if (!panelRef.current) return;

    const startNode = range.startContainer;
    const endNode = range.endContainer;

    const startRow = (startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode as HTMLElement)?.closest('tr');
    const endRow = (endNode.nodeType === Node.TEXT_NODE ? endNode.parentElement : endNode as HTMLElement)?.closest('tr');

    if (!startRow || !endRow || !panelRef.current.contains(startRow)) return;

    const lines: string[] = [];
    let currentRow: HTMLTableRowElement | null = startRow;

    while (currentRow && (!currentRow.isSameNode(endRow.nextElementSibling))) {
        if (currentRow.querySelector('td[colSpan="3"]')) {
            currentRow = currentRow.nextElementSibling as HTMLTableRowElement | null;
            continue;
        }

        const contentCell = currentRow.cells[2];
        if (contentCell) {
            let lineText = '';
            const fullLineText = contentCell.textContent || '';

            if (currentRow.isSameNode(startRow) && currentRow.isSameNode(endRow)) {
                lineText = selection.toString();
            } else if (currentRow.isSameNode(startRow)) {
                const tempRange = document.createRange();
                tempRange.selectNodeContents(contentCell);
                tempRange.setEnd(range.startContainer, range.startOffset);
                const startOffset = tempRange.toString().length;
                lineText = fullLineText.substring(startOffset);
            } else if (currentRow.isSameNode(endRow)) {
                const tempRange = document.createRange();
                tempRange.selectNodeContents(contentCell);
                tempRange.setStart(range.endContainer, range.endOffset);
                const unselectedLength = tempRange.toString().length;
                const endOffset = fullLineText.length - unselectedLength;
                lineText = fullLineText.substring(0, endOffset);
            } else {
                lineText = fullLineText;
            }
            lines.push(lineText);
        }

        currentRow = currentRow.nextElementSibling as HTMLTableRowElement | null;
    }

    const selectedText = lines.join('\n');

    const restoreSelection = () => {
        const currentSelection = window.getSelection();
        if (currentSelection) {
            currentSelection.removeAllRanges();
            currentSelection.addRange(savedRange);
        }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(selectedText)
            .then(restoreSelection)
            .catch(err => {
                console.error('Failed to copy text: ', err);
                restoreSelection();
            });
    } else {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = selectedText;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        } catch (err) {
            console.error('Fallback for copying text failed: ', err);
        }
        restoreSelection();
    }
  };

  const renderPanel = (type: 'left' | 'right') => {
    const renderedLines: React.ReactNode[] = [];
    let commonLineBuffer: typeof lines = [];

    const flushCommonLineBuffer = (isExpanded: boolean) => {
      if (commonLineBuffer.length > 0) {
        if (!isExpanded) {
          const foldIndex = renderedLines.length;
          renderedLines.push(
            <tr key={`fold-${foldIndex}`}>
              <td colSpan={3} style={{ textAlign: 'center', color: '#888', userSelect: 'none', cursor: 'pointer' }} onClick={() => setExpandedFolds(prev => new Set(prev).add(foldIndex))}>
                ... Show {commonLineBuffer.length} unchanged lines ...
              </td>
            </tr>
          );
        } else {
          commonLineBuffer.forEach((line, i) => renderedLines.push(renderLine(line, `common-${renderedLines.length}-${i}`)));
        }
        commonLineBuffer = [];
      }
    };

    const renderLine = (line: (typeof lines)[0], key: string) => {
      const lineNumber = type === 'left' ? line.leftNumber : line.rightNumber;
      const content = type === 'left' ? line.leftContent : line.rightContent;
      const indicator = type === 'left' ? (line.type === 'removed' || line.type === 'modified' ? '-' : ' ') : (line.type === 'added' || line.type === 'modified' ? '+' : ' ');

      let backgroundColor = 'transparent';
      if ((line.type === 'removed' || line.type === 'modified') && type === 'left') backgroundColor = '#ffeef0';
      if ((line.type === 'added' || line.type === 'modified') && type === 'right') backgroundColor = '#e6ffed';

      const rawText = type === 'left' ? line.leftRaw : line.rightRaw;

      return (
        <tr key={key} style={{ backgroundColor }}>
          <td style={{ userSelect: 'none', width: '40px', paddingRight: '10px', textAlign: 'right', color: '#888' }}>{lineNumber}</td>
          <td style={{ userSelect: 'none', width: '20px', textAlign: 'center', color: '#888' }}>{indicator}</td>
          <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content || ' '}</td>
        </tr>
      );
    };

    lines.forEach((line, index) => {
      if (showDiffOnly && line.type === 'common') {
        commonLineBuffer.push(line);
      } else {
        flushCommonLineBuffer(expandedFolds.has(renderedLines.length));
        renderedLines.push(renderLine(line, `line-${index}`));
      }
    });
    flushCommonLineBuffer(expandedFolds.has(renderedLines.length));

    return (
      <div
        ref={type === 'left' ? leftPanelRef : rightPanelRef}
        onScroll={() => handleScroll(type)}
        style={{ flex: 1, overflow: 'auto', borderRight: type === 'left' ? '1px solid #ddd' : 'none' }}
      onCopy={(e) => handleCopy(e, type)}
      >
        <table>
          <tbody>
            {renderedLines}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', fontFamily: 'monospace', fontSize: '14px' }}>
      {renderPanel('left')}
      {renderPanel('right')}
    </div>
  );
};

export default CustomDiffViewer;
